import React, { useState, useEffect } from 'react';
import { GLOBAL, ASSETS } from 'constants';
import WINDOW_OBJECT_KEYS from 'constants/windowObjectKeys';
import { config } from 'utils/config';
import { getTransmissionDescriptionFromFeaturesArray, getImageFromImagesObject } from 'utils/vehicles';
import utils from '@ehi/global-marketing-interface/utils';
import { getDateTimeObjFromTs } from '@ehi/global-marketing-interface/utils/app/dateTime';
import { i18n } from 'utils/i18n';
import { loadingRedirect, timeoutRedirect } from 'utils/redirect';
import { deleteSessionStorageKey } from 'utils/localStorage';
import RESERVATIONS from 'constants/reservations';
import cn from 'classnames';
import useSupportPhoneNumber from 'hooks/useSupportPhoneNumber';
import RentalBranchLocation from 'components/RentalBranchLocation';
import ReservationDetails from 'components/Confirmation/ReservationDetails';
import KeyRentalFacts from 'components/KeyRentalFacts';
import PoliciesComponent from 'components/PoliciesComponent';
import PrepayTermsModal from 'components/PrepayTermsModal';
import TaxesModal from 'components/modals/TaxesModal';
import ConfirmCancelReservationModal from 'components/modals/ConfirmCancelReservationModal';
import ModifyReservationModal from 'components/modals/ModifyReservationModal';
import PoliciesModal from 'components/modals/PoliciesModal';
import GuaranteedReservationModal from 'components/modals/GuaranteedReservationModal';
import ExtendRentalInformationModal from 'components/modals/ExtendRentalInformationModal';
import AfterHoursReturnInstructionsModal from 'components/modals/AfterHoursReturnInstructionsModal';
import ReturnDirectionsModal from 'components/modals/ReturnDirectionsModal';
import LearnMoreAboutRoadsideAssistanceModal from 'components/modals/LearnMoreAboutRoadsideAssistanceModal';
import RentalTermsAndConditionsModal from 'components/modals/RentalTermsAndConditionsModal';
import FlightDelayedModal from 'components/FlightDelay/FlightDelayedModal';
import UpdateFlightInfoModal from 'components/FlightDelay/UpdateFlightInfoModal';
import FlightConfirmedModal from 'components/FlightDelay/FlightConfirmedModal';
import CancelUpdatesModal from 'components/FlightDelay/CancelUpdatesModal';
import CurrencyConversionModal from 'components/modals/CurrencyConversionModal';
import RentalNotEligibleForModificationsModal from 'components/modals/RentalNotEligibleForModificationsModal';
import CheckInFirstNameConflictModal from 'components/modals/CheckInFirstNameConflictModal';
import RentalDetailsAfterHoursModal from 'components/modals/RentalDetailsAfterHoursModal';
import { loadingOverlay } from 'utils/dom';
import ConfirmationHeader from './ConfirmationHeader';
import { propTypes } from './confirmationPropTypes';

const Confirmation = (props) => {
  const {
    retrieveReservation,
    firstName,
    lastName,
    confirmationNumber,
    getSupportContactInfo,
    countryCode,
    policies,
    hasNoCancellationDetails,
    isFetchingSession,
    isFetchinRetrieveReservation,
    isCustomPathReservation,
    isFoundRental,
    breakpoint,
    reservationReady,
    isOneWayRental,
    vehicleDetails,
    maskedEmail,
    pickupLocation,
    returnLocation,
    pickupTime,
    returnTime,
    vehicleName,
    makeModelOrSimilarText,
    features,
    peopleCapacity,
    luggageCapacity,
    twoOrFourDoorText,
    images,
    vehicleCode,
    flightNumber,
    airline,
    priceSummary,
    showViewDetailsModal,
    extras,
    showLearnMoreModal,
    mileageInfo,
    prepaySelected,
    showCancelModal,
    isReservationCancelled,
    isCurrentTrip,
    afterHoursPickup,
    blockAssociateProfile,
    afterHoursReturn,
    showAHViewDetailsModal,
    additionalInformation,
    showModifyModal,
    contractDetails,
    hasLimitedInventoryExtra,
    hasAdaptativeEquipmentExtra,
    reservationStatus,
    maskedPhoneNumber,
    billingAccount,
    selectedBundleWithExtras,
    extrasWithoutBundles,
    partner_reward_program,
    numberOfExtrasSelected,
    isThirdPartyReservation,
    handlePrepayTermsModal,
    refundObject,
    thirdPartyReservationSupportNumber,
    memberNumber,
    modifyNotAllowedReasons,
    modifyCancelResExternalLink,
    guaranteedResCardRequired,
    showGuaranteedResModal,
    coupons,
    isTourReservation,
    openTermsAndConditionsModal,
    voucherExcludedValue,
    isTourVoucherReservation,
    vehicleSearchFilters,
    vehicleSubCategory,
    openFlightDelayModal,
    openUpdateFlightInfoModal,
    openCancelUpdatesmodal,
    selectedPaymentType,
    canCancelReservation,
    openFirstNameConflictModal,
    profileFirstname,
    unauthReservation,
    paymentMethod,
    appliedCancelFeeDetails,
    ticketNumber,
    supportLinks,
  } = props;

  // Fetching the support Contact Details in the parent level , to avoid multiple calls.
  useSupportPhoneNumber();
  const [selectedPolicyIndex, setSelectedPolicyIndex] = useState(null);
  const [currentValues, setCurrentValues] = useState(null);
  const [updateReservationState, setUpdateReservationState] = useState(null);

  const callRetrieveReservation = () => {
    Promise.all([
      retrieveReservation({
        firstName,
        lastName,
        confirmationNumber,
      }),
    ]).then(() => {
      setUpdateReservationState((prev) => !prev);
    });
  };

  useEffect(() => {
    if (countryCode && utils.isObjectEmpty(supportLinks)) {
      getSupportContactInfo(countryCode);
    }

    // Safari uses [back-forward cache] that shows the cached page when the user clicks the back button
    // so, we need to hide the loading overlay when the page is loaded from the cache
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        loadingOverlay(false);
      }
    });

    if (sessionStorage.getItem(config.reservationAssociatedToProfile)) {
      deleteSessionStorageKey(config.reservationAssociatedToProfile);
    }

    if (hasNoCancellationDetails) {
      callRetrieveReservation();
    }
  }, []);

  useEffect(() => {
    const isAuthorMode = config.getIsAuthorModeEnabled();

    // Calling function 'retrieveReservation' as 'Cancellation Modal' and 'Rental Policies' Language is not being updated with current language
    // on making an retrieve reservation everything works fine.

    const selectedLanguage = JSON.parse(sessionStorage.getItem(RESERVATIONS.SELECTED_LANGUGAGE_SESSION_STORAGE));

    if (
      selectedLanguage?.prevLang !== selectedLanguage?.currLang ||
      (hasNoCancellationDetails && !isFetchinRetrieveReservation && !updateReservationState)
    ) {
      callRetrieveReservation();
      // Clearing the session storage for selected langugage only if the langugage is changed/updated
      if (selectedLanguage?.prevLang !== selectedLanguage?.currLang) {
        sessionStorage.removeItem(RESERVATIONS.SELECTED_LANGUGAGE_SESSION_STORAGE);
      }
    }

    if (countryCode && utils.isObjectEmpty(supportLinks)) {
      getSupportContactInfo(countryCode);
    }

    if (!isFetchingSession && isCustomPathReservation) {
      // Redirect to restricted timeout page if it's a custom path reservation AWR-5859
      timeoutRedirect.setCustomRedirect(WINDOW_OBJECT_KEYS.RESTRICTED_TIMEOUT);
    }

    // If fetching is done, there is no reservation in session and it's not author mode,
    // redirect the user to Find a Reservation page
    if (!isFetchingSession && !confirmationNumber && !isAuthorMode) {
      loadingRedirect(WINDOW_OBJECT_KEYS.FIND_A_RESERVATION);
    }
  }, [countryCode, isFetchingSession, isFetchinRetrieveReservation]);

  const setIndexOfPolicyComponentViaPolicyCode = (policyCode) => () => {
    //  Finds index of the policy code
    const index = policies.findIndex((policy) => policy.code === policyCode);
    setSelectedPolicyIndex(index === -1 ? null : index);
  };

  const setIndexOfPolicyComponentViaIndex = (index) => {
    setSelectedPolicyIndex(index);
  };

  if (!reservationReady) {
    return null;
  }

  const isDriveHappyDeal = GLOBAL.HAPPY_DEAL_CODE === vehicleCode;
  const shouldShowPolicyComponent = policies.length !== 0;
  const isVehicleLimitedInventory = GLOBAL.HAS_LIMITED_INVENTORY_ON_CONFIRMATION_PAGE === reservationStatus;
  const hasLimitedInventoryConfirmationPage = isVehicleLimitedInventory || hasLimitedInventoryExtra;

  // These 4 variables are to determine if we render the flight delay update link (within 72 hours of before pickup or 24 hours after pickup)
  const now = getDateTimeObjFromTs(new Date());
  const beforePickupTime = getDateTimeObjFromTs(pickupTime).subtract(72, 'hours');
  const afterPickupTime = getDateTimeObjFromTs(pickupTime).add(24, 'hours');
  const displayFlightDelayLink = getDateTimeObjFromTs(now).isBetween(beforePickupTime, afterPickupTime);
  const isEligible = !modifyNotAllowedReasons && !isThirdPartyReservation;

  return (
    <main className={cn('confirmation', { cancellation: isReservationCancelled })}>
      <ConfirmationHeader
        confirmationNumber={confirmationNumber}
        ticketNumber={ticketNumber}
        firstName={firstName}
        lastName={lastName}
        maskedEmail={maskedEmail}
        breakpoint={breakpoint}
        isFoundRental={isFoundRental}
        showCancelModal={showCancelModal}
        showModifyModal={showModifyModal}
        isCancelled={isReservationCancelled}
        isCurrentTrip={isCurrentTrip}
        hasLimitedInventoryConfirmationPage={hasLimitedInventoryConfirmationPage}
        isVehicleLimitedInventory={isVehicleLimitedInventory}
        vehicleName={vehicleName}
        vehicleDetails={vehicleDetails}
        isThirdPartyReservation={isThirdPartyReservation}
        handlePrepayTermsModal={handlePrepayTermsModal}
        refundObject={refundObject}
        thirdPartyReservationSupportNumber={thirdPartyReservationSupportNumber}
        modifyNotAllowedReasons={modifyNotAllowedReasons}
        modifyCancelResExternalLink={modifyCancelResExternalLink}
        guaranteedResCardRequired={guaranteedResCardRequired}
        showGuaranteedResModal={showGuaranteedResModal}
        isTourReservation={isTourReservation}
        returnLocation={returnLocation}
        returnTime={returnTime}
        openFlightDelayModal={openFlightDelayModal}
        displayFlightDelayLink={displayFlightDelayLink}
        isEligible={isEligible}
        isTourVoucherReservation={isTourVoucherReservation}
        contractDetails={contractDetails}
        blockAssociateProfile={blockAssociateProfile}
        openFirstNameConflictModal={openFirstNameConflictModal}
        profileFirstname={profileFirstname}
        unauthReservation={unauthReservation}
        appliedCancelFeeDetails={appliedCancelFeeDetails}
        hasLimitedInventoryExtra={hasLimitedInventoryExtra}
        hasAdaptativeEquipmentExtra={hasAdaptativeEquipmentExtra}
      />

      <div className='theme--dark-blue'>
        {!isCurrentTrip && (
          <ReservationDetails
            setIndexOfPolicyComponentViaPolicyCode={setIndexOfPolicyComponentViaPolicyCode}
            oneWayRental={isOneWayRental}
            breakpoint={breakpoint}
            pickupLocation={pickupLocation}
            returnLocation={returnLocation}
            pickupTime={pickupTime}
            returnTime={returnTime}
            fullName={`${firstName} ${lastName}`}
            maskedEmail={maskedEmail}
            vehicleName={vehicleName}
            makeModelOrSimilarText={makeModelOrSimilarText}
            transmission={getTransmissionDescriptionFromFeaturesArray(features)}
            peopleCapacity={peopleCapacity}
            luggageCapacity={luggageCapacity}
            twoOrFourDoorText={twoOrFourDoorText}
            carImage={getImageFromImagesObject(images)}
            isDriveHappyDeal={isDriveHappyDeal}
            flightInfo={airline && flightNumber && `${airline} ${flightNumber}`}
            priceSummary={priceSummary}
            showViewDetailsModal={showViewDetailsModal}
            extras={extras}
            showLearnMoreModal={showLearnMoreModal}
            mileageInfo={mileageInfo}
            prepaySelected={prepaySelected}
            afterHoursReturn={afterHoursReturn}
            afterHoursPickup={afterHoursPickup}
            showAHViewDetailsModal={showAHViewDetailsModal}
            additionalInformation={additionalInformation}
            isCancelled={isReservationCancelled}
            contractDetails={contractDetails}
            isVehicleLimitedInventory={isVehicleLimitedInventory}
            maskedPhoneNumber={maskedPhoneNumber}
            billingAccount={billingAccount}
            selectedBundleWithExtras={selectedBundleWithExtras}
            extrasWithoutBundles={extrasWithoutBundles}
            partner_reward_program={partner_reward_program}
            numberOfExtrasSelected={numberOfExtrasSelected}
            isThirdPartyReservation={isThirdPartyReservation}
            modifyNotAllowedReasons={modifyNotAllowedReasons}
            memberNumber={memberNumber}
            guaranteedResCardRequired={guaranteedResCardRequired}
            coupons={coupons}
            isFoundRental={isFoundRental}
            policies={policies}
            openTermsAndConditionsModal={openTermsAndConditionsModal}
            voucherExcludedValue={voucherExcludedValue}
            isTourVoucherReservation={isTourVoucherReservation}
            vehicleSearchFilters={vehicleSearchFilters}
            vehicleSubCategory={vehicleSubCategory}
            vehicleCode={vehicleCode}
            openFlightDelayModal={openFlightDelayModal}
            displayFlightDelayLink={displayFlightDelayLink}
            isEligible={isEligible}
            selectedPaymentType={selectedPaymentType}
            features={features}
            isCurrentTrip={isCurrentTrip}
            isTourReservation={isTourReservation}
            showModifyModal={showModifyModal}
            modifyCancelResExternalLink={modifyCancelResExternalLink}
            thirdPartyReservationSupportNumber={thirdPartyReservationSupportNumber}
            showCancelModal={showCancelModal}
            canCancelReservation={canCancelReservation}
            paymentMethod={paymentMethod}
            appliedCancelFeeDetails={appliedCancelFeeDetails}
          />
        )}

        {!isReservationCancelled && <RentalBranchLocation confirmation={true} isOneWayRental={isOneWayRental} />}

        <section className='confirmation__policy-section-container'>
          <div className='confirmation__policy-container row'>
            <KeyRentalFacts />
            {shouldShowPolicyComponent && (
              <PoliciesComponent
                policies={policies}
                selectedPolicyIndex={selectedPolicyIndex}
                setIndexOfPolicyComponentViaIndex={setIndexOfPolicyComponentViaIndex}
                backgroundArtURL={ASSETS.RENTAL_POLICIES_PLACEHOLDER}
                backgroundArtAlt={i18n('policy_background_image_alt')}
              />
            )}
          </div>
        </section>
        <TaxesModal />

        <RentalDetailsAfterHoursModal />
        <CurrencyConversionModal />
        <PoliciesModal keyRentalFacts shouldFetchRental={false} />
        <PrepayTermsModal />
        {/* Flight Delay Modals here */}
        <FlightDelayedModal
          openUpdateFlightInfoModal={openUpdateFlightInfoModal}
          modifyNotAllowedReasons={modifyNotAllowedReasons}
          isThirdPartyReservation={isThirdPartyReservation}
        />
        <UpdateFlightInfoModal
          openCancelUpdatesmodal={openCancelUpdatesmodal}
          savedValues={currentValues}
          updateSavedValues={setCurrentValues}
        />
        <FlightConfirmedModal />
        <CancelUpdatesModal openUpdateFlightInfoModal={openUpdateFlightInfoModal} />
        {!policies.length && <RentalTermsAndConditionsModal />}
        {!isReservationCancelled && !isCurrentTrip && (
          <>
            <ConfirmCancelReservationModal />
            <ModifyReservationModal />
          </>
        )}
        {isReservationCancelled && <GuaranteedReservationModal />}
        {isCurrentTrip && (
          <>
            <ReturnDirectionsModal />
            <AfterHoursReturnInstructionsModal />
            <ExtendRentalInformationModal />
            <LearnMoreAboutRoadsideAssistanceModal />
            <RentalNotEligibleForModificationsModal />
          </>
        )}
        <CheckInFirstNameConflictModal closeAssociateFnameConflictModal={true} />
      </div>
    </main>
  );
};

Confirmation.propTypes = propTypes;

export default Confirmation;
